import {
  Alert,
  Box,
  Button,
  Grid,
  Header,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import { useMutation, useQuery } from '@apollo/client';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../../components/Notification';
import { usePrompt } from '../../../../components/Prompt/PromptProvider';
import {
  GET_RECEIVE_EXCEPTION,
  UPDATE_RECEIVE_EXCEPTION,
} from '../../../../services/receive-exception/queries';
import {
  ReceiveException,
  ReceiveExceptionStatus,
} from '../../../../services/receive-exception/types';
import {
  getCancelConfirmationPrompt,
  getResolveConfirmationPrompt,
  getUpdateFailureNotification,
  transformExceptionForView,
} from '../helper';
import { ReceiveExceptionView } from '../types';
import ExceptionDetails from './ExceptionDetails';
import CommentHistory from './ExceptionNotes';
import ResolverForm from './ResolverForm';
import { useQueryAppConfig } from '../appConfigUtils';

export type ViewExceptionMatchProps = {
  exceptionId: string;
};

export default function ViewException(): ReactElement {
  const { t } = useTranslation();
  const { addNotification, clearByGroup } = useNotification();
  const { showPrompt } = usePrompt();
  const { exceptionId } = useParams<ViewExceptionMatchProps>();
  const [data, setData] = useState<ReceiveExceptionView>();
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [isResolving, setIsResolving] = useState<boolean>(false);

  // Fetch exception type and resolver group info from AWS AppConfig
  const { fetchConfigError, exceptionTypes, loadingResolverConfig } = useQueryAppConfig(true);

  const { loading, error } = useQuery(GET_RECEIVE_EXCEPTION, {
    variables: { exceptionId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !exceptionId || exceptionTypes === [],
    onCompleted: (result) => {
      setData(transformExceptionForView(result.getReceiveException, exceptionTypes));
    },
  });

  const [updateException, { loading: updating }] = useMutation(UPDATE_RECEIVE_EXCEPTION, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setData(transformExceptionForView(result.updateReceiveException, exceptionTypes));
    },
  });

  const handleUpdate = async (update: Partial<ReceiveException>) => {
    try {
      clearByGroup('receive-exception-update');
      let promptResult;

      if (update.status === ReceiveExceptionStatus.RESOLVED) {
        setIsResolving(true);
        promptResult = await showPrompt(getResolveConfirmationPrompt(data as ReceiveExceptionView));
      }
      if (update.status === ReceiveExceptionStatus.CANCELLED) {
        setIsCancelling(true);
        promptResult = await showPrompt(getCancelConfirmationPrompt());
      }
      // no prompt for updating resolution
      if (!promptResult && update.status !== ReceiveExceptionStatus.PENDING_ACTION) {
        setIsResolving(false);
        setIsCancelling(false);
        return;
      }
      await updateException({
        variables: {
          request: {
            exceptionId: data?.exceptionId,
            ...update,
          },
        },
      });
      setIsResolving(false);
      setIsCancelling(false);
    } catch (e) {
      addNotification(getUpdateFailureNotification(e));
      setIsResolving(false);
      setIsCancelling(false);
    }
  };

  if (fetchConfigError) {
    return <Alert type="error">{fetchConfigError}</Alert>;
  }
  // Do not load page until exception type and resovler group info is done being loaded.
  if (loading || loadingResolverConfig) {
    return <Spinner size="big" />;
  }
  return (
    <div className="container-max-width">
      <SpaceBetween direction="vertical" size="l">
        <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
          <Header data-test-id="page-header" variant="h1">
            {data?.title}
          </Header>
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              {data?.isCancellable && (
                <Button
                  data-test-id="button-cancel-exception"
                  loading={isCancelling}
                  disabled={updating && !isCancelling}
                  variant="normal"
                  onClick={() => handleUpdate({ status: ReceiveExceptionStatus.CANCELLED })}>
                  {t('Cancel exception')}
                </Button>
              )}
              {data?.isResolvable && (
                <Button
                  data-test-id="button-resolve-exception"
                  loading={isResolving}
                  disabled={updating && !isResolving}
                  variant="primary"
                  onClick={() => handleUpdate({ status: ReceiveExceptionStatus.RESOLVED })}>
                  {t('Close exception')}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        </Grid>

        {error && <Alert type="error">{error.message}</Alert>}
        {!error && data && (
          <SpaceBetween direction="vertical" size="l">
            <ExceptionDetails data={data} />
            <Grid gridDefinition={[{ colspan: { default: 6 } }, { colspan: { default: 6 } }]}>
              <ResolverForm data={data} onSubmit={handleUpdate} />
              <CommentHistory data={data} />
            </Grid>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </div>
  );
}
